import { motion } from "framer-motion";
import { BadgeCheck, Download, Phone, Smartphone, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n";
import { PHONES, waLink, POSTERS } from "@/lib/site";
import { LogoMark } from "@/components/Logo";

const ease = [0.16, 1, 0.3, 1];

export default function AppHeader({ onDownload }) {
    const { t } = useLang();
    const a = t.app;

    return (
        <section id="top" className="relative bg-white">
            <div className="mx-auto max-w-3xl px-4 pt-8 sm:px-6 sm:pt-12">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease }}
                    className="flex items-start gap-4 sm:gap-5"
                >
                    <LogoMark className="h-20 w-20 shrink-0 shadow-lg shadow-emerald-900/10 sm:h-24 sm:w-24" />
                    <div className="min-w-0 pt-1">
                        <h1 className="font-display flex flex-wrap items-center gap-1.5 text-xl font-bold tracking-tight text-ink sm:text-3xl">
                            {a.name}
                            <BadgeCheck className="h-5 w-5 shrink-0 text-brand" aria-label="Verified business" />
                        </h1>
                        <p className="mt-1 text-sm font-semibold text-brand sm:text-base">{a.devLine}</p>
                        <p className="mt-1 text-xs text-slate-500 sm:text-sm">{a.metaLine}</p>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1, ease }}
                    className="mt-7 grid grid-cols-4 divide-x divide-slate-200 border-y border-slate-200 py-4 text-center"
                    data-testid="app-stats-row"
                >
                    {a.stats.map((s, i) => (
                        <div key={i} className="px-1">
                            <p className="font-display truncate text-sm font-bold text-ink sm:text-xl">{s.v}</p>
                            <p className="mt-0.5 truncate text-[10px] font-medium uppercase tracking-wide text-slate-500 sm:text-xs">
                                {s.l}
                            </p>
                        </div>
                    ))}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.18, ease }}
                >
                    <button
                        type="button"
                        onClick={onDownload}
                        data-testid="hero-apk-download-button"
                        className="mt-7 flex h-14 w-full items-center justify-center gap-2.5 rounded-full bg-brand text-lg font-bold text-white shadow-lg shadow-emerald-600/25 transition-all hover:-translate-y-0.5 hover:bg-brand-dark active:translate-y-0"
                    >
                        <Download className="h-5 w-5" />
                        {a.dlBtn}
                    </button>

                    <div className="mt-3 grid grid-cols-2 gap-3">
                        <a
                            href={`tel:${PHONES[0].tel}`}
                            data-testid="hero-call-primary-button"
                            className="flex h-12 items-center justify-center gap-2 rounded-full border-2 border-brand bg-white font-semibold text-brand transition-colors hover:bg-brand-soft"
                        >
                            <Phone className="h-4 w-4" />
                            {a.callBtn}
                        </a>
                        <a
                            href={waLink(PHONES[0].wa, "Hello Azmthullah Packers & Movers! I need a moving quote.")}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-button"
                            className="flex h-12 items-center justify-center gap-2 rounded-full bg-[#25D366] font-semibold text-white transition-all hover:-translate-y-0.5 hover:brightness-95"
                        >
                            <MessageCircle className="h-4 w-4" />
                            {a.waBtn}
                        </a>
                    </div>

                    <p className="mt-4 flex items-center gap-2 text-xs text-slate-500 sm:text-sm">
                        <Smartphone className="h-4 w-4 shrink-0 text-brand" />
                        {a.dlNote}
                    </p>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.28, ease }}
                className="mx-auto mt-8 max-w-3xl px-4 pb-10 sm:px-6"
            >
                <h2 className="font-display mb-3 text-lg font-bold tracking-tight text-ink sm:text-xl">
                    {a.shotsLabel}
                </h2>
                <div className="no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
                    {POSTERS.map((s, i) => (
                        <motion.div
                            key={i}
                            whileHover={{ y: -4 }}
                            transition={{ type: "spring", stiffness: 300, damping: 22 }}
                            className="aspect-square w-60 shrink-0 snap-start overflow-hidden rounded-2xl border border-emerald-900/10 shadow-md shadow-emerald-900/5 sm:w-72"
                        >
                            <img
                                src={s.src}
                                alt={s.alt}
                                loading={i === 0 ? "eager" : "lazy"}
                                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                            />
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
}
